/**
 * Tiny in-memory event bus used by the Google Maps loader, the
 * `AddressAutocomplete` component, and the `SiteMap` component to report
 * lifecycle moments (instance creation, listener registration, deferred
 * teardown, etc.) for the debug-only `MapsLifecyclePanel`.
 *
 * Production builds tree-shake the panel out, but the bus itself is
 * intentionally side-effect-light so leaving the `emit()` calls in the
 * shipped components is harmless.
 */

export type MapsLifecycleSource =
  | "loader"
  | "autocomplete"
  | "map"
  | "wizard";

export type MapsLifecycleAction =
  | "instance:create"
  | "instance:reuse"
  | "instance:dispose"
  | "instance:dispose-cancelled"
  | "listener:add"
  | "listener:add-skipped"
  | "listener:duplicate"
  | "listener:remove"
  | "listener:remove-skipped"
  | "cleanup:scheduled"
  | "cleanup:cancelled"
  | "cleanup:run"
  | "step:enter"
  | "step:leave"
  | "step:mount"
  | "error";

export interface MapsLifecycleEvent {
  id: number;
  ts: number;
  source: MapsLifecycleSource;
  action: MapsLifecycleAction;
  /** Free-form, human-readable detail (e.g. listener name, error message). */
  detail?: string;
}

type Listener = (events: ReadonlyArray<MapsLifecycleEvent>) => void;

const MAX_EVENTS = 200;
let nextId = 1;
const events: MapsLifecycleEvent[] = [];
const listeners = new Set<Listener>();

// Live counters maintained by callers via dedicated helpers below. The
// panel reads these to display "instance counts" without having to walk
// the event log.
const counters = {
  autocompleteInstances: 0,
  autocompleteListeners: 0,
  mapInstances: 0,
  mapListeners: 0,
};

export type MapsLifecycleCounters = typeof counters;

function notify() {
  // Pass a frozen snapshot so subscribers can safely keep references.
  const snapshot = events.slice();
  listeners.forEach((l) => {
    try {
      l(snapshot);
    } catch {
      /* ignore subscriber errors */
    }
  });
}

/**
 * Tracks listener keys that are currently considered "active" (added but
 * not yet removed) per source. We use this to surface duplicate listener
 * registrations — i.e. the same handler/action being added a second time
 * before its previous registration was removed. The key is derived from
 * the human-readable detail (e.g. "place_changed", "map:click",
 * "marker:dragend") so we don't need any extra plumbing at call sites.
 */
const activeListenerKeys = new Map<MapsLifecycleSource, Set<string>>();
let lastStepDetail: string | null = null;

function listenerKeyFromDetail(detail: string | undefined): string | null {
  if (!detail) return null;
  // Detail strings look like "place_changed (token #3)" or "map:click".
  // Strip a trailing "(...)" annotation so the same handler with a new
  // token id still hashes to the same key.
  const stripped = detail.replace(/\s*\(.*?\)\s*$/, "").trim();
  return stripped || null;
}

function trackListenerLifecycle(
  source: MapsLifecycleSource,
  action: MapsLifecycleAction,
  detail: string | undefined,
): { duplicate: boolean; activeBefore: number } {
  const key = listenerKeyFromDetail(detail);
  if (!key) return { duplicate: false, activeBefore: 0 };
  let set = activeListenerKeys.get(source);
  if (!set) {
    set = new Set();
    activeListenerKeys.set(source, set);
  }
  const sigKey = `${key}`;
  const activeBefore = set.size;
  if (action === "listener:add") {
    const duplicate = set.has(sigKey);
    set.add(sigKey);
    return { duplicate, activeBefore };
  }
  if (action === "listener:remove") {
    set.delete(sigKey);
  }
  return { duplicate: false, activeBefore };
}

export function emitMapsLifecycle(
  source: MapsLifecycleSource,
  action: MapsLifecycleAction,
  detail?: string,
) {
  // Duplicate-listener detection runs before the primary event is
  // appended so the warning row appears immediately above the redundant
  // `listener:add` entry in the timeline.
  let duplicateDetected = false;
  if (action === "listener:add" || action === "listener:remove") {
    const { duplicate } = trackListenerLifecycle(source, action, detail);
    duplicateDetected = duplicate;
    if (duplicate) {
      const dupEvt: MapsLifecycleEvent = {
        id: nextId++,
        ts: Date.now(),
        source,
        action: "listener:duplicate",
        detail: `duplicate registration for "${listenerKeyFromDetail(detail)}" (previous never removed)${
          lastStepDetail ? ` · last step: ${lastStepDetail}` : ""
        }`,
      };
      events.push(dupEvt);
      if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          `[maps:${source}] listener:duplicate`,
          dupEvt.detail,
        );
      }
    }
  } else if (action === "step:enter" || action === "step:mount") {
    lastStepDetail = detail ?? null;
    // On entering a new step, audit any listeners still flagged as
    // active. Anything left behind is a strong signal that the previous
    // step's component leaked a registration. We surface a single
    // synthetic warning event per dangling key so the panel timeline
    // ties the leak to the step transition that uncovered it.
    activeListenerKeys.forEach((set, src) => {
      set.forEach((leakedKey) => {
        const leakEvt: MapsLifecycleEvent = {
          id: nextId++,
          ts: Date.now(),
          source: src as MapsLifecycleSource,
          action: "listener:duplicate",
          detail: `dangling listener "${leakedKey}" carried into ${detail ?? "next step"}`,
        };
        events.push(leakEvt);
        if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(`[maps:${src}] listener:duplicate`, leakEvt.detail);
        }
      });
    });
  } else if (action === "step:leave") {
    lastStepDetail = detail ?? lastStepDetail;
  }

  const evt = { id: nextId++, ts: Date.now(), source, action, detail };
  events.push(evt);
  if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
  if (import.meta.env.DEV) {
    const tag = `[maps:${source}] ${action}`;
    const stamp = new Date(evt.ts).toISOString().slice(11, 23);
    if (action === "error") {
      // eslint-disable-next-line no-console
      console.warn(`${stamp} ${tag}`, detail ?? "");
    } else {
      // eslint-disable-next-line no-console
      console.debug(`${stamp} ${tag}`, detail ?? "");
    }
  }
  // Surface duplicate flag for callers/tests that want a fast-path
  // signal without re-scanning the event log.
  if (duplicateDetected && typeof globalThis !== "undefined") {
    (globalThis as Record<string, unknown>).__mapsLastDuplicate = {
      source,
      detail,
      ts: evt.ts,
    };
  }
  notify();
}

export function subscribeMapsLifecycle(listener: Listener): () => void {
  listeners.add(listener);
  // Push current state immediately so new subscribers don't render empty.
  listener(events.slice());
  return () => {
    listeners.delete(listener);
  };
}

export function getMapsLifecycleEvents(): ReadonlyArray<MapsLifecycleEvent> {
  return events.slice();
}

export function clearMapsLifecycle() {
  events.length = 0;
  activeListenerKeys.clear();
  lastStepDetail = null;
  notify();
}

export function getMapsLifecycleCounters(): MapsLifecycleCounters {
  return { ...counters };
}

export function adjustMapsLifecycleCounter(
  key: keyof MapsLifecycleCounters,
  delta: number,
) {
  counters[key] = Math.max(0, counters[key] + delta);
}

// ---------- Per-instance status registry ----------

/**
 * Live status snapshot for a single component instance. Consumers (the
 * dev panel, tests, ad-hoc DevTools probes) can read this to answer
 * "is the autocomplete initialized?" / "is there a place_changed
 * listener token currently held?" without reaching into component refs.
 */
export interface MapsInstanceStatus {
  /** Stable id assigned by the component on first registration. */
  id: string;
  source: MapsLifecycleSource;
  initialized: boolean;
  /** Whether the component currently holds a place_changed listener token. */
  hasPlaceListener?: boolean;
  /** Stringified token id (counter) for the held listener, if any. */
  placeListenerToken?: string | null;
  /** Whether the map instance ref currently points at a live map. */
  hasMapInstance?: boolean;
  /** Whether a deferred teardown timer is currently pending. */
  pendingTeardown?: boolean;
  /** Last update wall-clock ms timestamp. */
  updatedAt: number;
}

const instanceStatuses = new Map<string, MapsInstanceStatus>();
let nextTokenId = 1;

export function registerMapsInstance(
  source: MapsLifecycleSource,
  id: string,
): MapsInstanceStatus {
  const status: MapsInstanceStatus = {
    id,
    source,
    initialized: false,
    hasPlaceListener: false,
    placeListenerToken: null,
    hasMapInstance: false,
    pendingTeardown: false,
    updatedAt: Date.now(),
  };
  instanceStatuses.set(id, status);
  notify();
  return status;
}

export function updateMapsInstance(
  id: string,
  patch: Partial<Omit<MapsInstanceStatus, "id" | "source">>,
) {
  const current = instanceStatuses.get(id);
  if (!current) return;
  Object.assign(current, patch, { updatedAt: Date.now() });
  notify();
}

export function unregisterMapsInstance(id: string) {
  if (instanceStatuses.delete(id)) notify();
}

export function getMapsInstanceStatuses(): ReadonlyArray<MapsInstanceStatus> {
  return Array.from(instanceStatuses.values());
}

/** Generate a short opaque token id for a listener — purely for UI display. */
export function nextListenerTokenId(): string {
  return String(nextTokenId++);
}

